import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, Save, Languages, CheckCircle2, FileText, Wand2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PILLARS } from "../../data/pillars";

const LANGS = [
  { code: "bn", label: "বাংলা" },
  { code: "hi", label: "हिन्दी" },
];

const inputCls =
  "w-full rounded-xl border border-tara-gold/25 bg-white/85 px-4 py-2.5 text-sm text-tara-ink outline-none focus:border-tara-gold/60";

export default function ContentPanel() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("bn");
  const [slug, setSlug] = useState(PILLARS[0].slug);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [polishing, setPolishing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/content");
      setDocs(r.data || []);
    } catch { toast.error("Could not load translations"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = useMemo(
    () => docs.find((d) => d.lang === lang && d.slug === slug) || null,
    [docs, lang, slug]
  );

  useEffect(() => {
    if (!current) { setDraft(null); return; }
    setDraft({
      title: current.title || "",
      summary: current.summary || "",
      beginner: (current.beginner || []).join("\n\n"),
      detailed: (current.detailed || []).join("\n\n"),
      advanced: (current.advanced || []).join("\n\n"),
      faqs: (current.faqs || []).map((f) => `${f.q}\n${f.a}`).join("\n\n"),
    });
  }, [current]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const split = (v) => v.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);
      await api.put(`/admin/content/${lang}/${slug}`, {
        title: draft.title,
        summary: draft.summary,
        beginner: split(draft.beginner),
        detailed: split(draft.detailed),
        advanced: split(draft.advanced),
        faqs: split(draft.faqs).map((block) => {
          const [q, ...rest] = block.split("\n");
          return { q: q.trim(), a: rest.join(" ").trim() };
        }),
      });
      toast.success("Saved & marked reviewed");
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const polish = async () => {
    setPolishing(true);
    try {
      await api.post(`/admin/content/${lang}/${slug}/polish`);
      toast.success("Polished by AI — please review and save");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Polish failed");
    } finally {
      setPolishing(false);
    }
  };

  const statusFor = (code, s) => docs.find((d) => d.lang === code && d.slug === s);

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-8" data-testid="content-panel">
      <div className="space-y-5 h-fit lg:sticky lg:top-6">
        <div className="glass rounded-2xl p-5">
          <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep mb-4 flex items-center gap-2">
            <Languages className="h-3.5 w-3.5" /> Language
          </h3>
          <div className="flex gap-2">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                  lang === l.code
                    ? "bg-gradient-to-r from-tara-gold to-tara-vermilion text-white"
                    : "border border-tara-gold/25 text-tara-body hover:text-tara-gold"
                }`}
                data-testid={`content-lang-${l.code}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-5">
          <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep mb-4 flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" /> Knowledge pillars
          </h3>
          <ul className="space-y-1.5">
            {PILLARS.map((p) => {
              const doc = statusFor(lang, p.slug);
              return (
                <li key={p.slug}>
                  <button
                    onClick={() => setSlug(p.slug)}
                    className={`w-full text-left rounded-xl px-3.5 py-2.5 text-sm transition flex items-center justify-between gap-2 ${
                      slug === p.slug ? "bg-tara-gold/15 text-tara-ink" : "text-tara-body hover:bg-white/70"
                    }`}
                    data-testid={`content-pillar-${p.slug}`}
                  >
                    <span className="truncate">{p.title_en}</span>
                    {doc?.reviewed ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" title="Reviewed" />
                    ) : doc ? (
                      <span className="text-[9px] uppercase tracking-wider text-tara-gold shrink-0">AI</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wider text-tara-muted shrink-0">—</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-tara-gold animate-spin" /></div>
        ) : !draft ? (
          <div className="glass rounded-2xl p-12 text-center text-tara-muted" data-testid="content-empty">
            No translation stored for this pillar yet. Run the translation script, or add content here after it is generated.
          </div>
        ) : (
          <div className="glass-strong rounded-2xl p-6 space-y-5" data-testid="content-editor">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-serif text-lg font-bold text-tara-ink">
                {PILLARS.find((p) => p.slug === slug)?.title_en} · {LANGS.find((l) => l.code === lang)?.label}
              </h3>
              <div className="flex items-center gap-2.5">
                <button
                  onClick={polish}
                  disabled={polishing}
                  className="btn-ghost-gold flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold"
                  data-testid="content-polish-btn"
                  title="Rewrite this translation for native fluency"
                >
                  {polishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                  {polishing ? "Polishing…" : "Polish with AI"}
                </button>
                <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
                  current?.reviewed ? "text-green-800 bg-green-100 border-green-300" : "text-amber-800 bg-amber-100 border-amber-300"
                }`} data-testid="content-review-status">
                  {current?.reviewed ? "reviewed" : current?.source === "ai-polished" ? "ai polished" : "ai draft"}
                </span>
              </div>
            </div>

            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-tara-muted mb-1.5">Title</span>
              <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={inputCls} data-testid="content-title-input" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-tara-muted mb-1.5">Summary</span>
              <textarea value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} rows={3} className={inputCls} data-testid="content-summary-input" />
            </label>

            {["beginner", "detailed", "advanced"].map((section) => (
              <label className="block" key={section}>
                <span className="block text-[11px] uppercase tracking-[0.16em] text-tara-muted mb-1.5">
                  {section} — one paragraph per blank line
                </span>
                <textarea
                  value={draft[section]}
                  onChange={(e) => setDraft({ ...draft, [section]: e.target.value })}
                  rows={8}
                  className={`${inputCls} leading-relaxed`}
                  data-testid={`content-${section}-input`}
                />
              </label>
            ))}

            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-tara-muted mb-1.5">
                FAQs — question on the first line, answer below, blank line between items
              </span>
              <textarea value={draft.faqs} onChange={(e) => setDraft({ ...draft, faqs: e.target.value })} rows={8} className={`${inputCls} leading-relaxed`} data-testid="content-faqs-input" />
            </label>

            <button onClick={save} disabled={saving} className="btn-sacred flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold" data-testid="content-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save &amp; mark reviewed
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
